/** Query hooks tenant-svc (W8 control plane 5.2) — admin center (/platform/tenants…) và tenant tự phục vụ (/admin/profile|config|subscription|entitlements). */
import type { SubscriptionChangeStatus, TenantConfigChangeStatus, TenantListFilter } from '@kovix/api-client';
import { useQueries, useQuery } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';

export const tenantKeys = {
  all: ['tenant'] as const,
  list: (p: TenantListFilter) => [...tenantKeys.all, 'list', p] as const,
  stats: () => [...tenantKeys.all, 'stats'] as const,
  one: (id: string) => [...tenantKeys.all, 'one', id] as const,
  lifecycle: (id: string) => [...tenantKeys.all, 'lifecycle', id] as const,
  provisioning: (id: string) => [...tenantKeys.all, 'provisioning', id] as const,
  contacts: (id: string) => [...tenantKeys.all, 'contacts', id] as const,
  config: (id: string) => [...tenantKeys.all, 'config', id] as const,
  routing: (id: string) => [...tenantKeys.all, 'routing', id] as const,
  residency: (id: string) => [...tenantKeys.all, 'residency', id] as const,
  subscription: (id: string) => [...tenantKeys.all, 'subscription', id] as const,
  subscriptions: (id: string) => [...tenantKeys.all, 'subscriptions', id] as const,
  tenantChanges: (id: string) => [...tenantKeys.all, 'tenant-changes', id] as const,
  changes: (status?: SubscriptionChangeStatus) => [...tenantKeys.all, 'changes', status ?? 'ALL'] as const,
  entitlements: (id: string) => [...tenantKeys.all, 'entitlements', id] as const,
  quotas: (id: string) => [...tenantKeys.all, 'quotas', id] as const,
  me: () => [...tenantKeys.all, 'me'] as const,
  myContacts: () => [...tenantKeys.all, 'my-contacts'] as const,
  myConfig: () => [...tenantKeys.all, 'my-config'] as const,
  mySubscription: () => [...tenantKeys.all, 'my-subscription'] as const,
  myChanges: () => [...tenantKeys.all, 'my-changes'] as const,
  myEntitlements: () => [...tenantKeys.all, 'my-entitlements'] as const,
  myQuotas: () => [...tenantKeys.all, 'my-quotas'] as const,
};

const withEtag = <T extends { version: number }>(r: { data: T; etag: string | null }) => ({
  ...r.data,
  etag: r.etag ?? `"${r.data.version}"`,
});

// ---- platform ----
export const useTenants = (p: TenantListFilter) =>
  useQuery({
    queryKey: tenantKeys.list(p),
    queryFn: ({ signal }) => api.tenant.tenants({ ...p, limit: 200 }, signal).then((r) => r.data),
    placeholderData: (prev) => prev,
  });
/**
 * T6 V-PF-3 — gói hiện hành của từng tenant cho cột "Gói" ở /platform/tenants: danh sách tenant không mang planCode, nên hỏi
 * `filter[planCode]` theo TỪNG gói (số gói nhỏ) rồi ghép chỉ mục tenantId → planCode. Tenant không thuộc gói nào (chưa có subscription) → không có khóa.
 */
export function useTenantPlanIndex(planCodes: string[]) {
  const results = useQueries({
    queries: planCodes.map((planCode) => ({
      queryKey: tenantKeys.list({ planCode }),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.tenant.tenants({ planCode, limit: 200 }, signal).then((r) => r.data),
      staleTime: 60_000,
    })),
  });
  const index = new Map<string, string>();
  results.forEach((r, i) => {
    const code = planCodes[i];
    if (code) for (const t of r.data ?? []) index.set(t.id, code);
  });
  return { index, loading: results.some((r) => r.isLoading) };
}
export const useTenantStats = () =>
  useQuery({
    queryKey: tenantKeys.stats(),
    queryFn: ({ signal }) => api.tenant.stats(signal).then((r) => r.data),
    staleTime: 60_000,
  });
/** Q3-d — hồ sơ pháp lý đầy đủ (cột tenant + tenant_config legal.profile + billing.legal_address); etag = version tenant. */
export const useTenantLegalProfile = (id: string, enabled = true) =>
  useQuery({
    queryKey: [...tenantKeys.one(id), 'legal-profile'] as const,
    queryFn: ({ signal }) =>
      api.tenant.legalProfile(id, signal).then((r) => ({ ...r.data, etag: r.etag ?? `"${r.data.version}"` })),
    enabled: !!id && enabled,
  });
export const useTenant = (id: string) =>
  useQuery({
    queryKey: tenantKeys.one(id),
    queryFn: ({ signal }) => api.tenant.tenant(id, signal).then(withEtag),
    enabled: !!id,
  });
export const useTenantLifecycle = (id: string) =>
  useQuery({
    queryKey: tenantKeys.lifecycle(id),
    queryFn: ({ signal }) => api.tenant.lifecycle(id, 100, signal).then((r) => r.data),
    enabled: !!id,
  });
export const useProvisioning = (id: string, polling = false) =>
  useQuery({
    queryKey: tenantKeys.provisioning(id),
    queryFn: ({ signal }) => api.tenant.provisioning(id, signal).then((r) => r.data),
    enabled: !!id,
    refetchInterval: polling ? 5_000 : false,
  });
export const useTenantContacts = (id: string) =>
  useQuery({
    queryKey: tenantKeys.contacts(id),
    queryFn: ({ signal }) => api.tenant.contacts(id, signal).then((r) => r.data),
    enabled: !!id,
  });
export const useTenantConfig = (id: string) =>
  useQuery({
    queryKey: tenantKeys.config(id),
    queryFn: ({ signal }) => api.tenant.config(id, signal).then((r) => r.data),
    enabled: !!id,
  });
export const useTenantRouting = (id: string) =>
  useQuery({
    queryKey: tenantKeys.routing(id),
    queryFn: ({ signal }) => api.tenant.routing(id, signal).then((r) => r.data ?? null),
    enabled: !!id,
  });
export const useTenantResidency = (id: string) =>
  useQuery({
    queryKey: tenantKeys.residency(id),
    queryFn: ({ signal }) => api.tenant.residency(id, signal).then((r) => r.data),
    enabled: !!id,
  });
export const useTenantSubscription = (id: string) =>
  useQuery({
    queryKey: tenantKeys.subscription(id),
    queryFn: ({ signal }) =>
      api.tenant.subscription(id, signal).then((r) => (r.data ? withEtag({ data: r.data, etag: r.etag }) : null)),
    enabled: !!id,
  });
export const useTenantSubscriptions = (id: string) =>
  useQuery({
    queryKey: tenantKeys.subscriptions(id),
    queryFn: ({ signal }) => api.tenant.subscriptions(id, signal).then((r) => r.data),
    enabled: !!id,
  });
export const useTenantChanges = (id: string) =>
  useQuery({
    queryKey: tenantKeys.tenantChanges(id),
    queryFn: ({ signal }) => api.tenant.tenantChanges(id, undefined, signal).then((r) => r.data),
    enabled: !!id,
  });
export const useChangeQueue = (status?: SubscriptionChangeStatus) =>
  useQuery({
    queryKey: tenantKeys.changes(status),
    queryFn: ({ signal }) => api.tenant.changes(status, 200, signal).then((r) => r.data),
    placeholderData: (prev) => prev,
  });
export const useTenantEntitlements = (id: string) =>
  useQuery({
    queryKey: tenantKeys.entitlements(id),
    queryFn: ({ signal }) => api.tenant.entitlements(id, signal).then((r) => r.data),
    enabled: !!id,
  });
export const useTenantQuotas = (id: string) =>
  useQuery({
    queryKey: tenantKeys.quotas(id),
    queryFn: ({ signal }) => api.tenant.quotas(id, signal).then((r) => r.data),
    enabled: !!id,
  });

// ---- tenant tự phục vụ ----
export const useMyTenant = (enabled = true) =>
  useQuery({ queryKey: tenantKeys.me(), queryFn: ({ signal }) => api.tenant.me(signal).then(withEtag), enabled });
export const useMyContacts = () =>
  useQuery({
    queryKey: tenantKeys.myContacts(),
    queryFn: ({ signal }) => api.tenant.myContacts(signal).then((r) => r.data),
  });
export const useMyConfig = (enabled = true) =>
  useQuery({
    queryKey: tenantKeys.myConfig(),
    queryFn: ({ signal }) => api.tenant.myConfig(signal).then((r) => r.data),
    enabled,
  });
export const useMySubscription = () =>
  useQuery({
    queryKey: tenantKeys.mySubscription(),
    queryFn: ({ signal }) =>
      api.tenant.mySubscription(signal).then((r) => (r.data ? withEtag({ data: r.data, etag: r.etag }) : null)),
  });
export const useMyChanges = () =>
  useQuery({
    queryKey: tenantKeys.myChanges(),
    queryFn: ({ signal }) => api.tenant.myChanges(20, signal).then((r) => r.data),
  });
export const useMyEntitlements = () =>
  useQuery({
    queryKey: tenantKeys.myEntitlements(),
    queryFn: ({ signal }) => api.tenant.myEntitlements(signal).then((r) => r.data),
  });
export const useMyQuotas = () =>
  useQuery({
    queryKey: tenantKeys.myQuotas(),
    queryFn: ({ signal }) => api.tenant.myQuotas(signal).then((r) => r.data),
    refetchInterval: 60_000,
  });

// ---- T3 G4-2 / T3-B2: cấu hình tenant qua nháp → duyệt hai người (tenant-v1 1.4.0–1.5.0) ----
export const configChangeKeys = {
  myDraft: () => [...tenantKeys.all, 'config-change', 'me', 'draft'] as const,
  mine: (status?: TenantConfigChangeStatus) =>
    [...tenantKeys.all, 'config-change', 'me', 'list', status ?? 'ALL'] as const,
  platformDraft: (id: string) => [...tenantKeys.all, 'config-change', id, 'draft'] as const,
  platform: (id: string, status?: TenantConfigChangeStatus) =>
    [...tenantKeys.all, 'config-change', id, 'list', status ?? 'ALL'] as const,
};
/** Nháp đang mở của người đang đăng nhập (204 → null). */
export const useMyConfigDraft = (enabled = true) =>
  useQuery({
    queryKey: configChangeKeys.myDraft(),
    queryFn: ({ signal }) => api.tenant.myConfigDraft(signal).then((r) => r.data ?? null),
    enabled,
  });
export const useMyConfigChanges = (status?: TenantConfigChangeStatus, enabled = true) =>
  useQuery({
    queryKey: configChangeKeys.mine(status),
    queryFn: ({ signal }) => api.tenant.myConfigChanges(status, 100, signal).then((r) => r.data),
    enabled,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
export const useTenantConfigDraft = (id: string, enabled = true) =>
  useQuery({
    queryKey: configChangeKeys.platformDraft(id),
    queryFn: ({ signal }) => api.tenant.platformConfigDraft(id, signal).then((r) => r.data ?? null),
    enabled: !!id && enabled,
  });
export const useTenantConfigChanges = (id: string, status?: TenantConfigChangeStatus, enabled = true) =>
  useQuery({
    queryKey: configChangeKeys.platform(id, status),
    queryFn: ({ signal }) => api.tenant.platformConfigChanges(id, status, 100, signal).then((r) => r.data),
    enabled: !!id && enabled,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

// ---- T9 A37/A49 (tenant-v1 1.6.0) ----
/** Gói xuất toàn tenant — nền tảng chỉ thấy trạng thái + SHA-256; làm mới khi còn gom. */
export const useTenantExports = (id: string, enabled = true) =>
  useQuery({
    queryKey: [...tenantKeys.one(id), 'exports'] as const,
    queryFn: ({ signal }) => api.tenant.exports(id, signal).then((r) => r.data),
    enabled: !!id && enabled,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((e) => e.status === 'REQUESTED' || e.status === 'COLLECTING') ? 15_000 : false,
  });
export const useTenantPurgeRequests = (id: string, enabled = true) =>
  useQuery({
    queryKey: [...tenantKeys.one(id), 'purge-requests'] as const,
    queryFn: ({ signal }) => api.tenant.purgeRequests(id, signal).then((r) => r.data),
    enabled: !!id && enabled,
  });
export const useTenantLocaleSettings = (id: string, enabled = true) =>
  useQuery({
    queryKey: [...tenantKeys.one(id), 'locale-settings'] as const,
    queryFn: ({ signal }) => api.tenant.localeSettings(id, signal).then((r) => r.data),
    enabled: !!id && enabled,
  });
/** Quản trị tenant — gói xuất của chính tenant (đọc được khi đóng băng chỉ đọc). */
export const useMyTenantExports = (enabled = true) =>
  useQuery({
    queryKey: [...tenantKeys.all, 'me', 'exports'] as const,
    queryFn: ({ signal }) => api.tenant.myExports(signal).then((r) => r.data),
    enabled,
  });
export const useMyLocaleSettings = (enabled = true) =>
  useQuery({
    queryKey: [...tenantKeys.all, 'me', 'locale-settings'] as const,
    queryFn: ({ signal }) => api.tenant.myLocaleSettings(signal).then((r) => r.data),
    enabled,
  });

/** R5.3 — kênh Portal/App theo tenant. */
export const useMyPortalChannels = () =>
  useQuery({
    queryKey: [...tenantKeys.all, 'me', 'portal-channels'] as const,
    queryFn: ({ signal }) => api.tenant.myPortalChannels(signal).then((r) => r.data),
  });
